import { useEffect, useMemo, useState } from 'react';
import { useBlocker } from 'react-router';
import { Plus, X } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  PageShell,
  SectionCard,
  Skeleton,
  toast,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { useHasRole } from '@/providers/AuthProvider';
import { RecipientPickerDialog } from '../components/RecipientPickerDialog';
import { useRecipientSettingsQuery, useSaveRecipientSettingsMutation } from '../hooks/useBusinessInquiries';
import { INQUIRY_TYPES, INQUIRY_TYPE_LABELS } from '../lib/businessInquiryLabels';
import { BUSINESS_INQUIRY_RECIPIENT_WRITE_ROLES } from '../lib/businessInquiryRbac';
import type { InquiryType, RecipientAdmin, RecipientSettings } from '../types/businessInquiry';

type Assignments = Record<InquiryType, RecipientAdmin[]>;

const toAssignments = (settings: RecipientSettings): Assignments => {
  const out = Object.fromEntries(INQUIRY_TYPES.map((t) => [t, [] as RecipientAdmin[]])) as Assignments;
  for (const row of settings.types) if (row.type.code in out) out[row.type.code] = [...row.recipients];
  return out;
};

const idsOf = (a: Assignments) =>
  Object.fromEntries(INQUIRY_TYPES.map((t) => [t, a[t].map((r) => r.adminUserId)])) as Record<InquiryType, string[]>;

/** 지정은 남지만 메일은 활성 계정에만 간다. */
export const activeRecipientCount = (recipients: ReadonlyArray<RecipientAdmin>): number =>
  recipients.filter((r) => r.active === true && r.found !== false).length;

/**
 * QA-628 BO-03 비즈니스 문의 수신 설정 — 시스템 > 비즈니스 문의 수신 설정. 유형별 담당자를 고르고 [저장] 때 7개 유형을
 * 한 번에 보낸다(전체 교체). 저장 이후 접수되는 문의부터 적용. 담당자 이름 조회(user-service)가 실패하면 저장을 막는다.
 */
export const BusinessInquiryRecipientsPage = () => {
  const canWrite = useHasRole(BUSINESS_INQUIRY_RECIPIENT_WRITE_ROLES);
  const query = useRecipientSettingsQuery();
  const save = useSaveRecipientSettingsMutation();
  const [draft, setDraft] = useState<Assignments | null>(null);
  const [pickerType, setPickerType] = useState<InquiryType | null>(null);

  useEffect(() => {
    if (query.data) setDraft(toAssignments(query.data));
  }, [query.data]);

  const dirty = useMemo(
    () => !!draft && !!query.data && JSON.stringify(idsOf(draft)) !== JSON.stringify(idsOf(toAssignments(query.data))),
    [draft, query.data],
  );
  const lookupFailed = !!draft && INQUIRY_TYPES.some((t) => draft[t].some((r) => r.name === null));

  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const removeRecipient = (type: InquiryType, adminUserId: string) =>
    setDraft((d) => (d ? { ...d, [type]: d[type].filter((r) => r.adminUserId !== adminUserId) } : d));

  const addRecipients = (type: InquiryType, admins: RecipientAdmin[]) => {
    setDraft((d) => (d ? { ...d, [type]: [...d[type], ...admins.filter((a) => !d[type].some((r) => r.adminUserId === a.adminUserId))] } : d));
    setPickerType(null);
  };

  const onSave = () => {
    if (!draft) return;
    save.mutate(idsOf(draft), {
      onSuccess: () => toast.success('저장했습니다. 이후 접수되는 문의부터 적용됩니다.'),
      onError: (e) => toast.error(friendlyMessage(e, '저장하지 못했습니다.')),
    });
  };

  return (
    <PageShell
      title="비즈니스 문의 수신 설정"
      breadcrumb={[{ label: '시스템' }, { label: '비즈니스 문의 수신 설정' }]}
      actions={
        canWrite ? (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={!dirty || save.isPending}
              onClick={() => query.data && setDraft(toAssignments(query.data))}
              data-testid="recipients-cancel"
            >
              취소
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={!dirty || save.isPending || lookupFailed}
              onClick={onSave}
              data-testid="recipients-save"
            >
              저장
            </Button>
          </div>
        ) : null
      }
    >
      {query.isPending || !draft ? (
        query.isError ? (
          <p role="alert" className="text-[13px]" style={{ color: 'var(--status-danger-fg)' }}>
            {friendlyMessage(query.error, '수신 설정을 불러오지 못했습니다.')}
          </p>
        ) : (
          <Skeleton className="h-64" />
        )
      ) : (
        <>
          <SectionCard>
            <p className="text-[13px]">
              <strong>기본 수신 주소</strong> <code className="ml-2">{query.data?.defaultRecipient}</code>
            </p>
            <p className="mt-1 text-[12px]" style={{ color: 'var(--content-muted)' }}>
              문의 유형별로 지정된 활성 담당자에게 알림 메일이 발송됩니다. 활성 담당자가 없는 유형의 문의는 기본 수신 주소로 발송됩니다.
            </p>
            {lookupFailed ? (
              <p role="alert" className="mt-2 text-[12px]" style={{ color: 'var(--status-danger-fg)' }}>
                관리자 계정 정보를 불러오지 못해 담당자 이름이 보이지 않습니다. 잠시 후 새로고침한 뒤 저장해 주세요.
              </p>
            ) : null}
          </SectionCard>

          <SectionCard padded={false}>
            <table className="w-full text-[13px]" data-testid="recipients-table">
              <thead>
                <tr style={{ background: 'var(--surface-1)' }}>
                  <th className="w-36 px-4 py-2 text-left font-semibold">문의 유형</th>
                  <th className="px-4 py-2 text-left font-semibold">수신 담당자</th>
                  <th className="w-32 px-4 py-2 text-left font-semibold">활성 수신자</th>
                  <th className="w-32 px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {INQUIRY_TYPES.map((type) => {
                  const recipients = draft[type];
                  const active = activeRecipientCount(recipients);
                  return (
                    <tr key={type} className="border-t" style={{ borderColor: 'var(--divider-default)' }} data-testid={`recipients-row-${type}`}>
                      <td className="px-4 py-3 font-semibold">{INQUIRY_TYPE_LABELS[type]}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {recipients.length === 0 ? <span style={{ color: 'var(--content-muted)' }}>—</span> : null}
                          {recipients.map((r) => {
                            const inactive = r.active === false || r.found === false;
                            return (
                              <span
                                key={r.adminUserId}
                                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5"
                                style={{ borderColor: 'var(--divider-strong)', borderStyle: inactive ? 'dashed' : 'solid' }}
                                data-testid="recipient-chip"
                                data-inactive={inactive || undefined}
                              >
                                {r.name ?? r.adminUserId}
                                {r.loginId ? <span style={{ color: 'var(--content-muted)' }}>{r.loginId}</span> : null}
                                {r.found === false ? (
                                  <span className="text-[11px]" style={{ color: 'var(--status-danger-fg)' }}>
                                    삭제된 계정
                                  </span>
                                ) : r.active === false ? (
                                  <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                                    비활성
                                  </span>
                                ) : null}
                                {canWrite ? (
                                  <button
                                    type="button"
                                    aria-label={`${r.name ?? r.adminUserId} 지정 해제`}
                                    onClick={() => removeRecipient(type, r.adminUserId)}
                                  >
                                    <X size={12} aria-hidden="true" />
                                  </button>
                                ) : null}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3" data-testid={`recipients-active-${type}`}>
                        {active > 0 ? (
                          `${active}명`
                        ) : (
                          <span className="rounded px-1.5 py-0.5 text-[12px]" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
                            0명 · 기본 주소
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canWrite ? (
                          <Button type="button" variant="outline" size="sm" onClick={() => setPickerType(type)} data-testid={`recipients-add-${type}`}>
                            <Plus size={14} aria-hidden="true" />
                            담당자 추가
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </SectionCard>
        </>
      )}

      <RecipientPickerDialog
        open={pickerType !== null}
        typeLabel={pickerType ? INQUIRY_TYPE_LABELS[pickerType] : ''}
        assignedIds={new Set(pickerType && draft ? draft[pickerType].map((r) => r.adminUserId) : [])}
        onClose={() => setPickerType(null)}
        onAdd={(admins) => pickerType && addRecipients(pickerType, admins)}
      />

      <Dialog open={blocker.state === 'blocked'} onOpenChange={(o) => (!o ? blocker.reset?.() : undefined)}>
        <DialogContent className="max-w-[420px]">
          <DialogHeader>
            <DialogTitle>저장하지 않고 나갈까요?</DialogTitle>
            <DialogDescription>변경한 수신 담당자가 저장되지 않았습니다. 나가면 변경 내용이 사라집니다.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => blocker.reset?.()}>
              계속 편집
            </Button>
            <Button type="button" variant="primary" onClick={() => blocker.proceed?.()}>
              나가기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
